// BugTracker.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreateIcon from '@mui/icons-material/Create';
import { useUser } from '../components/userContext';
import { useThemeContext, THEME_CLASSIC } from '../theme/ThemeContext';
import {
    DrkDialog,
    DrkDialogCard,
    DrkInfoBox,
    DrkFormField
} from '../components/DrkComponents';
import html2canvas from 'html2canvas';
import { HelpIconButton } from '../pages/HelpInfoSystem';
import { useDropzone } from 'react-dropzone';
import ScreenshotEditor from './ScreenshotEditor'; // Import für den Screenshot-Editor

/**
 * BugTrackerButton - Button mit Bug-Icon, der den Bug-Tracker-Dialog öffnet
 */
export const BugTrackerButton = ({
    position = 'topRight',
    size = 'medium',
    inHeader = false,
    ...props
}) => {
    const [openBugDialog, setOpenBugDialog] = useState(false);
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    const positionStyles = {
        topRight: { position: 'absolute', top: 16, right: 16 },
        topLeft: { position: 'absolute', top: 16, left: 16 },
        bottomRight: { position: 'absolute', bottom: 16, right: 16 },
        bottomLeft: { position: 'absolute', bottom: 16, left: 16 },
        inline: { display: 'inline-flex', marginLeft: 8 }
    };

    const iconSize = {
        small: { size: 'small', iconSize: 18 },
        medium: { size: 'medium', iconSize: 24 },
        large: { size: 'large', iconSize: 32 }
    };

    // Bedingte Hintergrundfarbe: Nur im modernen Theme und wenn im Header
    const hoverBackgroundColor = !isClassic && inHeader
        ? '#fbe9e7' // heller, weicher Kontrast im Dialog-Header
        : !isClassic
            ? 'rgba(244, 155, 141, 0.1)'
            : 'transparent';

    const backgroundColor = !isClassic && inHeader
        ? '#ffccbc' // schöner warmer Ton beim Hover
        : !isClassic
            ? 'rgba(228, 100, 80, 0.2)'
            : 'rgba(0, 0, 0, 0.04)';

    const handleOpenBugDialog = () => {
        setOpenBugDialog(true);
    };

    const handleCloseBugDialog = () => {
        setOpenBugDialog(false);
    };

    return (
        <>
            <Tooltip title="Bug oder Wunsch melden">
                <IconButton
                    color="primary"
                    onClick={handleOpenBugDialog}
                    size={iconSize[size].size}
                    sx={{
                        ...positionStyles[position],
                        bgcolor: backgroundColor,
                        '&:hover': {
                            bgcolor: hoverBackgroundColor,
                        },
                        ...(inHeader && {
                            p: 0.75,
                            borderRadius: '50%',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                        }),
                        ...props.sx
                    }}
                    {...props}
                >
                    <BugReportIcon sx={{ fontSize: iconSize[size].iconSize }} />
                </IconButton>
            </Tooltip>

            {openBugDialog && (
                <BugReportDialog
                    open={openBugDialog}
                    onClose={handleCloseBugDialog}
                />
            )}
        </>
    );
};

/**
 * BugReportDialog - Dialog zur Meldung von Bugs
 */
export const BugReportDialog = ({ open, onClose }) => {
    const { user } = useUser();
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        reporter: user?.username || '',
        priority: 'MEDIUM',
        environment: ''
    });
    const [customFields, setCustomFields] = useState({
        // NEU: Standardwert für type hinzugefügt (als Pflichtfeld)
        type: ''
    });
    const [screenshot, setScreenshot] = useState(null);
    const [takingScreenshot, setTakingScreenshot] = useState(false);
    // Änderung: Array für mehrere Bilder anstatt einem einzelnen Bild
    const [uploadedImages, setUploadedImages] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const dialogRef = useRef(null);
    // Neuer State für Screenshot-Bearbeitung
    const [isEditingScreenshot, setIsEditingScreenshot] = useState(false);

    // Dynamische Felder Konfiguration
    const DYNAMIC_FIELDS = [
        {
            name: 'type', label: 'Wunsch oder Bug', type: 'select', options: [
                { value: 'wish', label: 'Wunsch' },
                { value: 'bug', label: 'Bug' },
            ],
            required: true
        },
        {
            name: 'priority', label: 'Priorität', type: 'select', options: [
                { value: 'LOW', label: 'Niedrig' },
                { value: 'MEDIUM', label: 'Mittel' },
                { value: 'HIGH', label: 'Hoch' },
            ]
        },
        { name: 'environment', label: 'Umgebung', type: 'text' },
        // ... beliebig weitere
    ];

    // Definition der erlaubten Bildtypen
    const ACCEPTED_IMAGE_TYPES = {
        'image/jpeg': [],
        'image/png': [],
        'image/gif': [],
        'image/webp': [],
        'image/bmp': [],
        'image/tiff': [],
        'image/svg+xml': []
    };

    // Formular-Eingabe-Handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Dynamische Felder Handler
    const handleDynamicChange = (fieldName, value) => {
        setCustomFields(prev => ({ ...prev, [fieldName]: value }));
    };

    // DropZone Konfiguration und Handler
    const onDrop = useCallback(acceptedFiles => {
        // Verarbeite jeden akzeptierten File
        const newImages = acceptedFiles.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve({
                        dataUrl: event.target.result,
                        file: file
                    });
                };
                reader.readAsDataURL(file);
            });
        });

        // Warte auf alle Promises und aktualisiere den State
        Promise.all(newImages).then(imageObjects => {
            setUploadedImages(prev => [...prev, ...imageObjects]);
        });
    }, []);

    // eslint-disable-next-line
    const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
        onDrop,
        accept: ACCEPTED_IMAGE_TYPES,
        noClick: true, // Deaktiviere den Klick auf die gesamte Zone
        noKeyboard: true // Deaktiviere Tastatur-Interaktion
    });

    // Screenshot-Funktion
    const handleTakeScreenshot = async () => {
        try {
            // Dialog-Element identifizieren (das erste DrkDialog-Element oder unsere spezifische Dialog-ID)
            const dialogElement = document.getElementById('bug-tracker-dialog');

            if (!dialogElement) {
                console.error('Dialog-Element nicht gefunden');
                setError('Screenshot konnte nicht erstellt werden: Dialog-Element nicht gefunden.');
                return;
            }

            // Status ändern
            setTakingScreenshot(true);

            // Dialog-Element temporär aus dem DOM entfernen anstatt nur auszublenden
            const parentNode = dialogElement.parentNode;
            const nextSibling = dialogElement.nextSibling;
            parentNode.removeChild(dialogElement);

            // Kurze Verzögerung, um sicherzustellen, dass der Dialog komplett entfernt ist
            await new Promise(resolve => setTimeout(resolve, 300));

            try {
                // Screenshot mit html2canvas erstellen
                const canvas = await html2canvas(document.body, {
                    logging: false,
                    useCORS: true,
                    // Ausdrücklich andere Dialog-Elemente ausschließen
                    ignoreElements: (element) => element.classList.contains('MuiDialog-root')
                });

                const screenshotDataUrl = canvas.toDataURL('image/png');

                // Screenshot setzen
                setScreenshot(screenshotDataUrl);
                // Nach erfolgreichem Screenshot direkt in den Bearbeitungsmodus wechseln
                setIsEditingScreenshot(true);
            } catch (screenshotError) {
                console.error('Fehler beim Erstellen des Screenshots:', screenshotError);
                setError('Fehler beim Erstellen des Screenshots: ' + screenshotError.message);
            }

            // Dialog-Element wieder ins DOM einfügen
            if (nextSibling) {
                parentNode.insertBefore(dialogElement, nextSibling);
            } else {
                parentNode.appendChild(dialogElement);
            }

            // Status zurücksetzen
            setTakingScreenshot(false);
        } catch (err) {
            console.error('Fehler beim Erstellen des Screenshots:', err);
            setError('Fehler beim Erstellen des Screenshots: ' + err.message);
            setTakingScreenshot(false);
        }
    };

    // Screenshot löschen
    const handleClearScreenshot = () => {
        setScreenshot(null);
        setIsEditingScreenshot(false);
    };

    // Handler für bearbeiteten Screenshot speichern
    const handleSaveEditedScreenshot = (editedScreenshotUrl) => {
        setScreenshot(editedScreenshotUrl);
        setIsEditingScreenshot(false);
    };

    // Handler für Screenshot-Bearbeitung abbrechen
    const handleCancelScreenshotEdit = () => {
        // Statt confirm() einfach direkt abbrechen
        // In einer erweiterten Version könnte hier ein React-Modal statt confirm eingesetzt werden
        setIsEditingScreenshot(false);
    };

    // Bild hochladen über klassischen File-Input
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Mehrere Dateien verarbeiten
            const newImages = files.map(file => {
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        resolve({
                            dataUrl: event.target.result,
                            file: file
                        });
                    };
                    reader.readAsDataURL(file);
                });
            });

            // Warte auf alle FileReader-Operationen
            Promise.all(newImages).then(imageObjects => {
                setUploadedImages(prev => [...prev, ...imageObjects]);
            });
        }
    };

    // Ein bestimmtes hochgeladenes Bild löschen
    const handleRemoveImage = (index) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    // State für Erfolgsbenachrichtigung
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Bug-Report absenden
    const handleSubmit = async () => {
        try {
            // NEU: Überprüfen, ob der Typ ausgewählt wurde (Pflichtfeld)
            if (!customFields.type) {
                setError('Bitte wählen Sie einen Typ (Wunsch oder Bug) aus.');
                return;
            }

            setSubmitting(true);
            setError(null);

            // Reguläre Formulardaten mit benutzerdefinierten Feldern kombinieren
            const bugData = {
                ...formData,
                customFields
            };

            // FormData für Datei-Upload erstellen
            const formDataToSend = new FormData();
            formDataToSend.append('bugData', JSON.stringify(bugData));

            // Screenshot hinzufügen, falls vorhanden
            if (screenshot) {
                // Base64 zu Blob konvertieren
                const screenshotBlob = await fetch(screenshot).then(r => r.blob());
                formDataToSend.append('screenshot', screenshotBlob, 'screenshot.png');
            }

            // Alle hochgeladenen Bilder hinzufügen
            if (uploadedImages.length > 0) {
                // Hier der Schlüssel zur Behebung des Fehlers: 
                // Verwende einen eigenen Array-Namen im FormData
                for (let i = 0; i < uploadedImages.length; i++) {
                    const imageBlob = await fetch(uploadedImages[i].dataUrl).then(r => r.blob());
                    formDataToSend.append('uploadedImages', imageBlob, uploadedImages[i].file.name);
                }
            }

            // An Server senden
            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/bug-reports`, {
                method: 'POST',
                headers: {
                    'Authorization': user.api_key,
                },
                body: formDataToSend
            });

            if (!response.ok) {
                throw new Error('Fehler beim Senden des Bug-Reports');
            }

            // Erfolgsstatus setzen
            setSubmitSuccess(true);

            // Formular zurücksetzen (im Hintergrund)
            setFormData({
                title: '',
                description: '',
                reporter: user?.username || '',
                priority: 'MEDIUM',
                environment: ''
            });
            setCustomFields({
                type: '' // Typen zurücksetzen
            });
            setScreenshot(null);
            setUploadedImages([]);

            // Nach 3 Sekunden den Dialog schließen
            setTimeout(() => {
                setSubmitSuccess(false);
                onClose();
            }, 3000);
        } catch (err) {
            console.error('Fehler beim Senden des Bug-Reports:', err);
            setError('Fehler beim Senden des Bug-Reports: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Effect zum Aktualisieren des Reporter-Namens, wenn sich der Benutzer ändert
    useEffect(() => {
        if (user?.username) {
            setFormData(prev => ({ ...prev, reporter: user.username }));
        }
    }, [user]);

    // Rendering-Funktionen
    const renderScreenshotPreview = () => {
        if (!screenshot) return null;

        return (
            <Box sx={{ mt: 2, mb: 2, position: 'relative' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Screenshot Vorschau:
                </Typography>

                {isEditingScreenshot ? (
                    // Screenshot-Editor-Komponente
                    <ScreenshotEditor
                        screenshotUrl={screenshot}
                        onSave={handleSaveEditedScreenshot}
                        onCancel={handleCancelScreenshotEdit}
                        isClassic={isClassic}
                    />
                ) : (
                    // Normale Screenshot-Vorschau
                    <Box
                        sx={{
                            position: 'relative',
                            maxHeight: '400px', // Größere Vorschau
                            overflow: 'auto',
                            border: '1px solid #ddd',
                            borderRadius: isClassic ? '4px' : '8px',
                        }}
                    >
                        <img
                            src={screenshot}
                            alt="Screenshot"
                            style={{ width: '100%', height: 'auto' }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            display: 'flex',
                            gap: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '4px',
                            padding: '2px'
                        }}>
                            <Tooltip title="Screenshot bearbeiten">
                                <IconButton
                                    color='primary'
                                    size="small"
                                    onClick={() => setIsEditingScreenshot(true)}
                                    sx={{
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#f0f0f0',
                                    '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : '#e0e0e0' }
                                    }}
                                >
                                    <CreateIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Screenshot löschen">
                                <IconButton
                                    color='primary'
                                    size="small"
                                    onClick={handleClearScreenshot}
                                    sx={{
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#f0f0f0',
                                    '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : '#e0e0e0' }
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    const renderUploadedImagesPreview = () => {
        if (uploadedImages.length === 0) return null;

        return (
            <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Hochgeladene Bilder ({uploadedImages.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {uploadedImages.map((image, index) => (
                        <Box
                            key={`uploaded-image-${index}`}
                            sx={{
                                position: 'relative',
                                width: '150px',
                                height: '150px',
                                overflow: 'hidden',
                                border: '1px solid #ddd',
                                borderRadius: isClassic ? '4px' : '8px',
                            }}
                        >
                            <img
                                src={image.dataUrl}
                                alt={`Uploaded ${index}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            <IconButton
                                aria-label="Bild löschen"
                                onClick={() => handleRemoveImage(index)}
                                sx={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#f0f0f0',
                                    '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : '#e0e0e0' },
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    padding: '4px',
                                }}
                                size="small"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Typography
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    padding: '2px 8px',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {image.file.name}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };

    // DropZone Rendering
    const renderDropZone = () => {
        return (
            <Box
                {...getRootProps()}
                sx={{
                    mt: 2,
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.400',
                    borderRadius: isClassic ? '4px' : '8px',
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: isDragActive
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                }}
            >
                <input {...getInputProps()} accept="image/*" />
                <Typography color={isDragActive ? 'primary' : 'textSecondary'}>
                    {isDragActive
                        ? 'Bild hier ablegen...'
                        : 'Ziehe Bilder hierher oder klicke auf "Bild hochladen"'}
                </Typography>
            </Box>
        );
    };

    // Dialog-Aktionen
    const dialogActions = (
        <>
            <Button color="inherit" onClick={onClose}>
                Abbrechen
            </Button>
            <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.title || !formData.description || !customFields.type || submitting || isEditingScreenshot}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
                Absenden
            </Button>
        </>
    );

    return (
        <DrkDialog
            ref={dialogRef}
            id="bug-tracker-dialog"
            open={open}
            onClose={isEditingScreenshot ? null : onClose}
            title={takingScreenshot ? "Screenshot wird erstellt..." : (isEditingScreenshot ? "Screenshot bearbeiten" : "Bug oder Wunsch melden")}
            maxWidth="lg"
            fullWidth
            actions={!takingScreenshot && !isEditingScreenshot ? dialogActions : null}
        >
            <HelpIconButton pageId="bug-reporter" position="topRight" inHeader={true} />

            <DrkDialogCard>
                {error && (
                    <DrkInfoBox color="error" sx={{ mb: 2 }}>
                        <Typography color="error">{error}</Typography>
                    </DrkInfoBox>
                )}

                {submitSuccess && (
                    <DrkInfoBox color="secondary" sx={{ mb: 2 }}>
                        <Typography variant="h6" color="secondary">Vielen Dank für Ihre Mitarbeit!</Typography>
                        <Typography>
                            Ihr Bug-Report wurde erfolgreich übermittelt.
                            Unser Team wird benachrichtigt und wird Ihr Anliegen schnellstmöglich bearbeiten.
                            Bei Bedarf werden wir uns mit Ihnen in Verbindung setzen.
                        </Typography>
                    </DrkInfoBox>
                )}

                {!submitSuccess && !isEditingScreenshot && (<>
                    {/* Zeile 1: Titel (75%) und Gemeldet von (25%) */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Box sx={{ flex: 3 }}> {/* 75% width */}
                            <DrkFormField
                                label="Titel"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                fullWidth
                                required
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}> {/* 25% width */}
                            <DrkFormField
                                label="Gemeldet von"
                                name="reporter"
                                value={formData.reporter}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                disabled
                            />
                        </Box>
                    </Box>

                    {/* Zeile 2: Beschreibung (unverändert) */}
                    <DrkFormField
                        label="Beschreibung"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                        fullWidth
                        required
                        margin="normal"
                    />

                    {/* Zeile 3: Dynamische Felder in zwei Spalten */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        {DYNAMIC_FIELDS.map((field) => (
                            <Box key={field.name} sx={{ flex: '1 1 calc(20% - 16px)', minWidth: 250 }}>
                                <DrkFormField
                                    label={field.label}
                                    type={field.type}
                                    value={customFields[field.name] || ''}
                                    onChange={(e) => {
                                        const val = field.type === 'select'
                                            ? e.target.value
                                            : e.target.value;
                                        handleDynamicChange(field.name, val);
                                    }}
                                    options={field.options || []}
                                    fullWidth
                                    required={field.required}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Screenshot & Upload Bereich */}
                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Bilder hinzufügen
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {takingScreenshot ? (
                                <Box sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    py: 4
                                }}>
                                    <CircularProgress size={40} sx={{ mb: 2 }} />
                                    <Typography>
                                        Screenshot wird erstellt, bitte warten...
                                    </Typography>
                                </Box>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<CameraAltIcon />}
                                    onClick={handleTakeScreenshot}
                                    disabled={takingScreenshot}
                                >
                                    Screenshot erstellen
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Bild hochladen
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/*"
                                onChange={handleImageUpload}
                                multiple
                            />
                        </Box>
                    </Box>
                </>)}

                {/* Screenshot-Editor oder normale Formularansicht */}
                {isEditingScreenshot ? (
                    renderScreenshotPreview()
                ) : (
                    <>
                        {/* DropZone für Drag & Drop */}
                        {!submitSuccess && renderDropZone()}

                        {/* Vorschau Bereiche */}
                        {renderScreenshotPreview()}
                        {renderUploadedImagesPreview()}
                    </>
                )}
            </DrkDialogCard>
        </DrkDialog>
    );

};

const defaultExport = { BugTrackerButton, BugReportDialog };
export default defaultExport;
